import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileCheck, Filter, Search, ChevronDown, ChevronUp, Star, 
  Camera, MessageSquare, User, Clock, MapPin, CheckCircle, XCircle,
  FileText, Download, Eye, Calendar, Truck, ShoppingCart, Printer,
  FolderPlus, Send, Trash2, Car
} from 'lucide-react';
import { Shirt } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { completionFormsAPI, jobWorkflowAPI } from '../../services/api';
import { toast } from 'sonner';
import PDFPreviewModal from './PDFPreviewModal';
import api from '../../services/api';
import DOMPurify from 'dompurify';

const CompletedFormsTab = () => {
  const [forms, setForms] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'rides', 'laundry', 'courier', 'errands'
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedForm, setExpandedForm] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [pdfPreview, setPdfPreview] = useState({ isOpen: false, blob: null, isLoading: false, error: null, title: '', filename: '' });

  useEffect(() => {
    fetchForms();
  }, [filter]);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') {
        params.service_type = filter;
      }
      const res = await completionFormsAPI.getAll(params);
      setForms(res.data.forms || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error('Failed to fetch completion forms:', err);
      toast.error('Failed to load completion forms');
    } finally {
      setLoading(false);
    }
  };

  // Download form as PDF function
  const handleDownloadPDF = async (form) => {
    try {
      const response = await completionFormsAPI.downloadPDF(form.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `completion_form_${form.id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Form downloaded as PDF');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download form as PDF');
    }
  };

  // Preview form as PDF function
  const handlePreviewPDF = async (form) => {
    setPdfPreview({ 
      isOpen: true, 
      blob: null, 
      isLoading: true, 
      error: null, 
      title: `Job Completion Form - ${form.service_name}`,
      filename: `completion_form_${form.id.slice(0, 8).toUpperCase()}.pdf`
    });
    
    try {
      const response = await completionFormsAPI.downloadPDF(form.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfPreview(prev => ({ ...prev, blob, isLoading: false }));
    } catch (error) {
      console.error('Failed to load PDF preview:', error);
      setPdfPreview(prev => ({ ...prev, isLoading: false, error: 'Failed to load document' }));
    }
  };

  const closePdfPreview = () => {
    setPdfPreview({ isOpen: false, blob: null, isLoading: false, error: null, title: '', filename: '' });
  };

  // Download signature PDF function
  const handleDownloadSignaturePDF = async (form) => {
    try {
      const bookingId = form.booking_id;
      if (!bookingId) {
        toast.error('No booking associated with this form');
        return;
      }
      
      const response = await jobWorkflowAPI.downloadSignaturePDF(bookingId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signature_${form.service_name?.replace(/\s+/g, '_') || 'service'}_${form.completed_at?.slice(0, 10) || 'unknown'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Signature PDF downloaded');
    } catch (error) {
      console.error('Failed to download signature PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to download signature PDF');
    }
  };

  // Print form function
  const handlePrintForm = (form) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Job Completion Form - ${DOMPurify.sanitize(form.service_name || '')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #D4AF37; padding-bottom: 20px; }
          .header h1 { color: #D4AF37; margin: 0; }
          .section { margin-bottom: 25px; }
          .section h3 { background: #f5f5f5; padding: 10px; margin: 0 0 15px 0; border-left: 4px solid #D4AF37; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          .info-item { padding: 8px 0; border-bottom: 1px solid #eee; }
          .info-label { font-weight: bold; color: #666; font-size: 12px; }
          .info-value { color: #333; }
          .checklist { list-style: none; padding: 0; }
          .checklist li { padding: 8px 0; border-bottom: 1px solid #eee; display: flex; align-items: center; gap: 10px; }
          .checklist .done { color: green; }
          .checklist .not-done { color: red; }
          .signature-box { border: 1px solid #ddd; padding: 15px; margin: 10px 0; text-align: center; }
          .signature-img { max-height: 80px; }
          .photos-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .photos-grid img { width: 100%; height: 100px; object-fit: cover; border: 1px solid #ddd; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>NC Help Hub</h1>
          <h2>Job Completion Form</h2>
          <p>Form ID: ${form.id}</p>
        </div>
        
        <div class="section">
          <h3>Service Information</h3>
          <div class="info-grid">
            <div class="info-item"><span class="info-label">Service:</span> <span class="info-value">${form.service_name}</span></div>
            <div class="info-item"><span class="info-label">Type:</span> <span class="info-value">${getServiceTypeLabel(form.service_type)}</span></div>
            <div class="info-item"><span class="info-label">Date:</span> <span class="info-value">${formatDate(form.scheduled_date)}</span></div>
            <div class="info-item"><span class="info-label">Location:</span> <span class="info-value">${form.address}, ${form.city}, ${form.state}</span></div>
          </div>
        </div>

        <div class="section">
          <h3>Personnel</h3>
          <div class="info-grid">
            <div class="info-item"><span class="info-label">Worker:</span> <span class="info-value">${form.worker_name}</span></div>
            <div class="info-item"><span class="info-label">Customer:</span> <span class="info-value">${form.customer_name}</span></div>
          </div>
        </div>

        <div class="section">
          <h3>Task Checklist (${form.checklist_completed_count}/${form.checklist_total_count} completed)</h3>
          <ul class="checklist">
            ${(form.checklist || []).map(item => `
              <li>
                <span class="${item.completed ? 'done' : 'not-done'}">${item.completed ? '✓' : '✗'}</span>
                ${item.task || item.name}
                ${item.notes ? `<em style="color:#666; font-size:12px;"> - ${item.notes}</em>` : ''}
              </li>
            `).join('')}
          </ul>
        </div>

        ${form.worker_notes ? `
        <div class="section">
          <h3>Worker Notes</h3>
          <p>${form.worker_notes}</p>
        </div>
        ` : ''}

        ${form.customer_cooperation_rating ? `
        <div class="section">
          <h3>Customer Cooperation Rating</h3>
          <p>${'★'.repeat(form.customer_cooperation_rating)}${'☆'.repeat(5 - form.customer_cooperation_rating)} (${form.customer_cooperation_rating}/5)</p>
          ${form.cooperation_notes ? `<p>${form.cooperation_notes}</p>` : ''}
        </div>
        ` : ''}

        <div class="section">
          <h3>Signatures</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div class="signature-box">
              <p><strong>Worker Signature</strong></p>
              <p>${form.worker_signature?.name || 'N/A'}</p>
              ${form.worker_signature?.signature_data ? `<img src="${form.worker_signature.signature_data}" class="signature-img" />` : ''}
              <p style="font-size:12px; color:#666;">Signed: ${formatDate(form.worker_signature?.signed_at)}</p>
            </div>
            <div class="signature-box">
              <p><strong>Customer Signature</strong></p>
              ${form.customer_signature ? `
                <p>${form.customer_signature.name}</p>
                ${form.customer_signature.signature_data ? `<img src="${form.customer_signature.signature_data}" class="signature-img" />` : ''}
                <p style="font-size:12px; color:#666;">Signed: ${formatDate(form.customer_signature.signed_at)}</p>
              ` : '<p style="color:orange;">Customer not available</p>'}
            </div>
          </div>
        </div>

        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; text-align: center;">
          <p>Form completed: ${formatDate(form.completed_at)} at ${formatTime(form.completed_at)}</p>
          <p>NC Help Hub - Professional Home Services</p>
        </div>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(DOMPurify.sanitize(printContent, { WHOLE_DOCUMENT: true }));
    printWindow.document.close();
    printWindow.print();
  };

  // Save form to customer's file
  const handleSaveToCustomerFile = async (form) => {
    try {
      await api.post('/forms/save-to-customer', {
        form_id: form.id,
        customer_id: form.customer_id,
        form_type: 'completion_form',
        service_type: form.service_type,
        service_name: form.service_name,
        completed_date: form.completed_at
      });
      toast.success(`Form saved to ${form.customer_name}'s file!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save to customer file');
    }
  };

  // Email form to customer
  const handleEmailToCustomer = async (form) => {
    try {
      await api.post(`/forms/completion/${form.id}/email-customer`);
      toast.success(`Form emailed to ${form.customer_name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to email form');
    }
  };

  // Delete completion form
  const handleDeleteForm = async (form) => {
    if (!window.confirm(`Delete this completion form for ${form.service_name}? This action cannot be undone.`)) return;
    try {
      await completionFormsAPI.delete(form.id);
      toast.success('Form deleted successfully');
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete form');
    }
  };

  const filteredForms = forms.filter(form => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      form.worker_name?.toLowerCase().includes(search) ||
      form.customer_name?.toLowerCase().includes(search) ||
      form.service_name?.toLowerCase().includes(search) ||
      form.address?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getServiceTypeLabel = (type) => {
    switch(type) {
      case 'rides': return 'Rides';
      case 'laundry': return 'Laundry';
      case 'courier': return 'Courier';
      case 'errands': return 'Errands';
      default: return type;
    }
  };

  const getServiceTypeIcon = (type) => {
    switch(type) {
      case 'rides': return <Car className="w-6 h-6 text-purple-500" />;
      case 'laundry': return <Shirt className="w-6 h-6 text-[#F4C2C2]" />;
      case 'courier': return <Truck className="w-6 h-6 text-blue-500" />;
      case 'errands': return <ShoppingCart className="w-6 h-6 text-pink-500" />;
      default: return <FileCheck className="w-6 h-6 text-[var(--text-secondary)]" />;
    }
  };

  const getServiceTypeBgColor = (type) => {
    switch(type) {
      case 'rides': return 'bg-purple-500/15';
      case 'laundry': return 'bg-[#F4C2C2]/15';
      case 'courier': return 'bg-blue-500/15';
      case 'errands': return 'bg-pink-500/15';
      default: return 'bg-[var(--background-subtle)]';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
            <FileCheck className="w-7 h-7 text-[var(--primary)]" />
            Completed Job Forms
          </h2>
          <p className="text-[var(--text-muted)] mt-1">
            View signed checklists, photos, and ratings from completed jobs
          </p>
        </div>
        <Button onClick={fetchForms} variant="outline" className="flex items-center gap-2">
          <span className="animate-spin-slow">
            <FileCheck className="w-4 h-4" />
          </span>
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div 
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            filter === 'all' ? 'bg-[var(--primary)] text-white ring-2 ring-[var(--primary)]' : 'bg-[var(--background-paper)] hover:bg-[var(--background-subtle)]'
          }`}
          onClick={() => setFilter('all')}
          data-testid="filter-all"
        >
          <FileCheck className={`w-5 h-5 mb-2 ${filter === 'all' ? 'text-white' : 'text-[var(--primary)]'}`} />
          <p className="text-xl font-bold">{summary.total_forms || 0}</p>
          <p className={`text-xs ${filter === 'all' ? 'text-white/80' : 'text-[var(--text-muted)]'}`}>All Forms</p>
        </div>

        <div 
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            filter === 'rides' ? 'bg-purple-500 text-white ring-2 ring-purple-500' : 'bg-purple-500/10 hover:bg-purple-500/15'
          }`}
          onClick={() => setFilter('rides')}
          data-testid="filter-rides"
        >
          <Car className={`w-5 h-5 mb-2 ${filter === 'rides' ? 'text-white' : 'text-purple-500'}`} />
          <p className="text-xl font-bold">{summary.rides_forms || 0}</p>
          <p className={`text-xs ${filter === 'rides' ? 'text-white/80' : 'text-purple-500'}`}>Rides</p>
        </div>

        <div 
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            filter === 'laundry' ? 'bg-[#F4C2C2] text-black ring-2 ring-[#F4C2C2]' : 'bg-[#F4C2C2]/10 hover:bg-[#F4C2C2]/15'
          }`}
          onClick={() => setFilter('laundry')}
          data-testid="filter-laundry"
        >
          <Shirt className={`w-5 h-5 mb-2 ${filter === 'laundry' ? 'text-black' : 'text-[#F4C2C2]'}`} />
          <p className="text-xl font-bold">{summary.laundry_forms || 0}</p>
          <p className={`text-xs ${filter === 'laundry' ? 'text-black/80' : 'text-[#F4C2C2]'}`}>Laundry</p>
        </div>

        <div 
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            filter === 'courier' ? 'bg-blue-500 text-white ring-2 ring-blue-500' : 'bg-blue-500/10 hover:bg-blue-500/15'
          }`}
          onClick={() => setFilter('courier')}
          data-testid="filter-courier"
        >
          <Truck className={`w-5 h-5 mb-2 ${filter === 'courier' ? 'text-white' : 'text-blue-500'}`} />
          <p className="text-xl font-bold">{summary.courier_forms || 0}</p>
          <p className={`text-xs ${filter === 'courier' ? 'text-white/80' : 'text-blue-500'}`}>Courier</p>
        </div>

        <div 
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            filter === 'errands' ? 'bg-pink-500 text-white ring-2 ring-pink-500' : 'bg-pink-500/10 hover:bg-pink-500/15'
          }`}
          onClick={() => setFilter('errands')}
          data-testid="filter-errands"
        >
          <ShoppingCart className={`w-5 h-5 mb-2 ${filter === 'errands' ? 'text-white' : 'text-pink-500'}`} />
          <p className="text-xl font-bold">{summary.errands_forms || 0}</p>
          <p className={`text-xs ${filter === 'errands' ? 'text-white/80' : 'text-pink-500'}`}>Errands</p>
        </div>

        <div 
          className={`p-4 rounded-xl cursor-pointer transition-all ${
            filter === 'tasks' ? 'bg-green-500 text-white ring-2 ring-green-500' : 'bg-green-500/10 hover:bg-green-500/15'
          }`}
          onClick={() => setFilter('tasks')}
          data-testid="filter-tasks"
        >
          <Wrench className={`w-5 h-5 mb-2 ${filter === 'tasks' ? 'text-white' : 'text-green-500'}`} />
          <p className="text-xl font-bold">{summary.tasks_forms || 0}</p>
          <p className={`text-xs ${filter === 'tasks' ? 'text-white/80' : 'text-green-500'}`}>Tasks</p>
        </div>

        <div className="p-4 rounded-xl bg-amber-500/10">
          <CheckCircle className="w-5 h-5 mb-2 text-amber-600" />
          <p className="text-xl font-bold text-amber-600">{summary.with_customer_signature || 0}</p>
          <p className="text-xs text-amber-600">Signed</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder="Search by worker, customer, service, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-forms"
        />
      </div>

      {/* Forms List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-[var(--primary)]">Loading forms...</div>
        </div>
      ) : filteredForms.length === 0 ? (
        <div className="text-center py-12 bg-[var(--background-subtle)] rounded-xl">
          <FileCheck className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-[var(--text-muted)]">No completion forms found</p>
          <p className="text-sm text-gray-400">Forms will appear here after workers complete jobs</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredForms.map((form) => (
            <motion.div
              key={form.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-[var(--background-paper)] rounded-xl border overflow-hidden transition-all ${
                expandedForm === form.id ? 'ring-2 ring-[var(--primary)]' : 'hover:shadow-md'
              }`}
            >
              {/* Form Header */}
              <div 
                className="p-4 cursor-pointer"
                onClick={() => setExpandedForm(expandedForm === form.id ? null : form.id)}
                data-testid={`form-card-${form.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Service Type Badge */}
                    <div className={`p-3 rounded-xl ${getServiceTypeBgColor(form.service_type)}`}>
                      {getServiceTypeIcon(form.service_type)}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{form.service_name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          form.service_type === 'private_concierge' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                          form.service_type === 'social_engagement' ? 'bg-[#F4C2C2]/15 text-purple-700' :
                          'bg-[var(--success)]/15 text-green-700'
                        }`}>
                          {getServiceTypeLabel(form.service_type)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[var(--text-muted)]">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {form.worker_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(form.scheduled_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {form.city}, {form.state}
                        </span>
                      </div>
                      <p className="text-sm mt-1">
                        <span className="text-[var(--text-muted)]">Customer:</span>{' '}
                        <span className="font-medium">{form.customer_name}</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Checklist Progress */}
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {form.checklist_completed_count}/{form.checklist_total_count} tasks
                      </p>
                      <div className="w-24 h-2 bg-[var(--background-subtle)] rounded-full mt-1">
                        <div 
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${(form.checklist_completed_count / form.checklist_total_count) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Rating */}
                    {form.customer_cooperation_rating && (
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                        <span className="font-medium">{form.customer_cooperation_rating}</span>
                      </div>
                    )}

                    {/* Signature Status */}
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      form.customer_signature ? 'bg-[var(--success)]/15 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {form.customer_signature ? 'Signed' : 'Worker Only'}
                    </div>

                    {/* PDF Download Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPDF(form);
                      }}
                      className="gap-1 text-[var(--primary)] border-[var(--primary)]/30 hover:bg-[var(--primary)]/10"
                      data-testid={`download-pdf-${form.id}`}
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </Button>

                    {/* Preview Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewPDF(form);
                      }}
                      className="gap-1 text-blue-500 border-blue-300 hover:bg-blue-500/10"
                      data-testid={`preview-pdf-${form.id}`}
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </Button>

                    {/* Print Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintForm(form);
                      }}
                      className="gap-1"
                      data-testid={`print-form-${form.id}`}
                    >
                      <Printer className="w-4 h-4" />
                      <span className="hidden sm:inline">Print</span>
                    </Button>

                    {/* Save to Customer File */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveToCustomerFile(form);
                      }}
                      className="gap-1 text-green-500 border-green-500/30 hover:bg-green-500/10"
                      data-testid={`save-to-file-${form.id}`}
                    >
                      <FolderPlus className="w-4 h-4" />
                      <span className="hidden sm:inline">Save</span>
                    </Button>

                    {/* Email to Customer */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEmailToCustomer(form);
                      }}
                      className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-500/10"
                      data-testid={`email-form-${form.id}`}
                    >
                      <Send className="w-4 h-4" />
                      <span className="hidden sm:inline">Email</span>
                    </Button>

                    {/* Delete Form */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteForm(form);
                      }}
                      className="gap-1 text-[var(--error)] border-[var(--error)]/30 hover:bg-[var(--error)]/10"
                      data-testid={`delete-form-${form.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Delete</span>
                    </Button>

                    {/* Expand Icon */}
                    {expandedForm === form.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedForm === form.id && (
                <div className="border-t p-4 bg-[var(--background-subtle)]">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Checklist */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[var(--primary)]" />
                        Checklist Items
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {form.checklist_items?.map((item) => (
                          <div 
                            key={item.label || item.id}
                            className={`p-2 rounded-lg flex items-start gap-2 ${
                              item.completed ? 'bg-[var(--success)]/10' : 'bg-[var(--error)]/10'
                            }`}
                          >
                            {item.completed ? (
                              <CheckCircle className="w-5 h-5 text-[var(--success)] flex-shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="w-5 h-5 text-[var(--error)] flex-shrink-0 mt-0.5" />
                            )}
                            <div>
                              <p className={`text-sm ${item.completed ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                                {item.label}
                              </p>
                              {item.notes && (
                                <p className="text-xs text-[var(--text-muted)] mt-1">Note: {item.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Photos */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Camera className="w-5 h-5 text-[var(--primary)]" />
                        Before & After Photos
                      </h4>
                      
                      {form.before_photos?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm text-[var(--text-muted)] mb-2">Before:</p>
                          <div className="flex flex-wrap gap-2">
                            {form.before_photos.map((url) => (
                              <img 
                                key={url}
                                src={url}
                                alt="Before photo"
                                className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80"
                                onClick={() => window.open(url, '_blank')}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {form.after_photos?.length > 0 && (
                        <div>
                          <p className="text-sm text-[var(--text-muted)] mb-2">After:</p>
                          <div className="flex flex-wrap gap-2">
                            {form.after_photos.map((url) => (
                              <img 
                                key={url}
                                src={url}
                                alt="After photo"
                                className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80"
                                onClick={() => window.open(url, '_blank')}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {(!form.before_photos?.length && !form.after_photos?.length) && (
                        <p className="text-sm text-gray-400 italic">No photos uploaded</p>
                      )}
                    </div>
                  </div>

                  {/* Notes & Rating Section */}
                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    {/* Notes */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-[var(--primary)]" />
                        Worker Notes
                      </h4>
                      {form.worker_notes ? (
                        <p className="text-sm bg-[var(--background-paper)] p-3 rounded-lg">{form.worker_notes}</p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No notes provided</p>
                      )}

                      {form.additional_comments && (
                        <div className="mt-3">
                          <p className="text-xs text-[var(--text-muted)] mb-1">Additional Comments:</p>
                          <p className="text-sm bg-[var(--background-paper)] p-3 rounded-lg">{form.additional_comments}</p>
                        </div>
                      )}
                    </div>

                    {/* Cooperation Rating */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Star className="w-5 h-5 text-[var(--primary)]" />
                        Customer Cooperation
                      </h4>
                      {form.customer_cooperation_rating ? (
                        <div className="bg-[var(--background-paper)] p-3 rounded-lg">
                          <div className="flex items-center gap-1 mb-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star 
                                key={star}
                                className={`w-5 h-5 ${
                                  form.customer_cooperation_rating >= star 
                                    ? 'text-yellow-500 fill-yellow-500' 
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                            <span className="ml-2 font-medium">
                              {form.customer_cooperation_rating}/5
                            </span>
                          </div>
                          {form.cooperation_notes && (
                            <p className="text-sm text-[var(--text-secondary)]">{form.cooperation_notes}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic">Not rated</p>
                      )}
                    </div>
                  </div>

                  {/* Signatures */}
                  <div className="mt-6 pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold">Signatures</h4>
                      {form.booking_id && (form.worker_signature?.signature_data || form.customer_signature?.signature_data) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadSignaturePDF(form)}
                          className="text-xs"
                          data-testid={`download-signature-pdf-${form.id}`}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Signature PDF
                        </Button>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Worker Signature */}
                      <div className="bg-[var(--background-paper)] p-4 rounded-lg border">
                        <p className="text-sm text-[var(--text-muted)] mb-2">Worker Signature</p>
                        <p className="font-medium">{form.worker_signature?.name}</p>
                        {form.worker_signature?.signature_data && (
                          <img 
                            src={form.worker_signature.signature_data}
                            alt="Worker Signature"
                            className="h-16 mt-2 border rounded"
                          />
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Signed: {formatTime(form.worker_signature?.signed_at)} {formatDate(form.worker_signature?.signed_at)}
                        </p>
                      </div>

                      {/* Customer Signature */}
                      <div className={`p-4 rounded-lg border ${
                        form.customer_signature ? 'bg-[var(--background-paper)]' : 'bg-[var(--warning)]/10'
                      }`}>
                        <p className="text-sm text-[var(--text-muted)] mb-2">Customer Signature</p>
                        {form.customer_signature ? (
                          <>
                            <p className="font-medium">{form.customer_signature.name}</p>
                            {form.customer_signature.signature_data && (
                              <img 
                                src={form.customer_signature.signature_data}
                                alt="Customer Signature"
                                className="h-16 mt-2 border rounded"
                              />
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              Signed: {formatTime(form.customer_signature.signed_at)} {formatDate(form.customer_signature.signed_at)}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-amber-700 font-medium">Customer not available</p>
                            {form.customer_unavailable_reason && (
                              <p className="text-sm text-amber-600 mt-1">
                                Reason: {form.customer_unavailable_reason}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Completion Time */}
                  <div className="mt-4 text-right text-sm text-[var(--text-muted)]">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Form completed: {formatDate(form.completed_at)} at {formatTime(form.completed_at)}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={pdfPreview.isOpen}
        onClose={closePdfPreview}
        pdfBlob={pdfPreview.blob}
        title={pdfPreview.title}
        filename={pdfPreview.filename}
        isLoading={pdfPreview.isLoading}
        error={pdfPreview.error}
      />
    </div>
  );
};

export default CompletedFormsTab;
